import { FC, RefObject, forwardRef, useMemo } from "react";
import { TreeSelect } from "antd";
import SearchIcon from "@mui/icons-material/Search";
import "./styles.css";
import { Box, useTheme } from "@mui/material";
import { getStyles } from "./styles";
import { SELECT_ALL } from "../../config";

interface IProps {
  title: string;
  value?: any | null;
  optionList: any[];
  isDisabled?: boolean;
  withSelectAll?: boolean;
  onChange?: (newValue: string[]) => void;
}

const SELECT_ALL_VALUE = "__SELECT_ALL__";

export const CheckboxAutocompleteMultiple: FC<IProps> = forwardRef<
  RefObject<any>,
  IProps
>(
  (
    { title, value, onChange, optionList, isDisabled, withSelectAll },
    ref: any
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const styles = useMemo(() => getStyles(isDark), [isDark]);

    const transformData = (data: any[], parentKey: string) => {
      return data.map((item, index) => {
        const key = parentKey ? `${parentKey}-${index}` : `${index}`;
        const {
          name,
          children,
          id,
          first_name,
          last_name,
          position,
          patronymic,
        } = item;

        const treeNode: any = {
          label:
            name || `${last_name} ${first_name} ${patronymic} (${position})`,
          value: id,
        };

        if ("children" in item) {
          treeNode.children = transformData(children, key);
        }

        return treeNode;
      });
    };

    const transformedData = transformData(optionList, "");

    const getLeafNodeIds = (data: any[]): string[] => {
      const ids: string[] = [];
      data.forEach((item) => {
        if (!item.children) {
          ids.push(item.value);
        } else {
          ids.push(...getLeafNodeIds(item.children));
        }
      });
      return ids;
    };

    const allValues = useMemo(
      () => getLeafNodeIds(transformedData),
      [transformedData]
    );
    const isAllSelected =
      value?.length === allValues.length && allValues.length > 0;

    const handleSelectAll = (checked: boolean) => {
      if (checked) {
        onChange?.(allValues);
      } else {
        onChange?.([]);
      }
    };

    const treeData = useMemo(() => {
      if (!withSelectAll) return transformedData;
      return [
        {
          label: SELECT_ALL,
          value: SELECT_ALL_VALUE,
          parent: "",
          children: transformedData,
        },
      ];
    }, [transformedData, withSelectAll]);

    const treeValue = useMemo(() => {
      if (!withSelectAll) return value;
      return isAllSelected ? [SELECT_ALL_VALUE, ...allValues] : value;
    }, [value, isAllSelected, allValues, withSelectAll]);

    const onChangePermission = (newValue: string[], _info: any, data: any) => {
      const triggerValue = data.triggerValue;

      if (withSelectAll && triggerValue === SELECT_ALL_VALUE) {
        const isChecked = newValue.includes(SELECT_ALL_VALUE);
        handleSelectAll(isChecked);
        return;
      }

      const leafNodeIds = getLeafNodeIds(transformedData);
      const filteredIds = newValue.filter((id) => leafNodeIds.includes(id));
      onChange?.(filteredIds);
    };

    const tProps = {
      treeData,
      value: treeValue,
      disabled: isDisabled,
      onChange: onChangePermission,
      showSearch: true,
      treeIcon: false,
      treeCheckable: true,
      placeholder: title,
      suffixIcon: <SearchIcon sx={styles.suffixIcon} />,
      showCheckedStrategy: TreeSelect.SHOW_ALL,
      style: { width: "100%" },
      filterTreeNode: (inputValue: string, treeNode: any) =>
        treeNode.label?.toLowerCase().includes(inputValue.toLowerCase()),
    };

    return (
      <Box sx={styles.wrapper}>
        <TreeSelect ref={ref} {...tProps} />
      </Box>
    );
  }
);
